import React, { useState, useEffect } from 'react'
import {
  Snackbar,
  Alert,
  AlertTitle,
  Typography,
  IconButton,
  Collapse,
  Card,
  CardContent
} from '@material-ui/core'
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon
} from '@material-ui/icons'

// Estilos inline para el componente de notificación

/**
 * Componente de notificaciones mejorado para tours
 * Proporciona feedback visual para todas las acciones del usuario
 */
const TourNotification = ({ 
  open, 
  message, 
  type = 'info', 
  title, 
  duration = 5000,
  onClose,
  autoHide = true,
  persistent = false
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    if (open) {
      setIsVisible(true)
      setProgress(100)
      
      if (autoHide && !persistent) {
        const timer = setInterval(() => {
          setProgress(prev => {
            if (prev <= 0) {
              clearInterval(timer)
              handleClose()
              return 0
            }
            return prev - (100 / (duration / 100))
          })
        }, 100)
        
        return () => clearInterval(timer)
      }
    } else {
      setIsVisible(false)
    }
  }, [open, duration, autoHide, persistent])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose && onClose()
    }, 300)
  }

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon color="primary" />
      case 'error':
        return <ErrorIcon color="error" />
      case 'warning':
        return <WarningIcon color="action" />
      case 'info':
      default:
        return <InfoIcon color="primary" />
    }
  }

  const getCardStyle = () => {
    const baseStyle = {
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      border: 'none',
      marginBottom: 16,
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out'
    }
    
    switch (type) {
      case 'success':
        return { ...baseStyle, borderLeft: '4px solid #4caf50', backgroundColor: '#f1f8e9' }
      case 'error':
        return { ...baseStyle, borderLeft: '4px solid #f44336', backgroundColor: '#ffebee' }
      case 'warning':
        return { ...baseStyle, borderLeft: '4px solid #ff9800', backgroundColor: '#fff3e0' }
      case 'info':
      default:
        return { ...baseStyle, borderLeft: '4px solid #2196f3', backgroundColor: '#e3f2fd' }
    }
  }

  const getColor = () => {
    switch (type) {
      case 'success':
        return '#4caf50'
      case 'error':
        return '#f44336'
      case 'warning':
        return '#ff9800'
      case 'info':
      default:
        return '#2196f3'
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, maxWidth: 400 }}>
      <Collapse in={isVisible} direction="left">
        <Card style={getCardStyle()}>
          <CardContent style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: 8 }}>
                  {getIcon()}
                </div>
                <Typography style={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {title || type.toUpperCase()}
                </Typography>
              </div>
              <IconButton 
                size="small" 
                onClick={handleClose}
                style={{ padding: 4 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            
            <Typography style={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
              {message}
            </Typography>
            
            {autoHide && !persistent && (
              <div 
                style={{ 
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  backgroundColor: getColor(),
                  width: `${progress}%`,
                  transition: 'width 0.1s linear'
                }}
              />
            )}
          </CardContent>
        </Card>
      </Collapse>
    </div>
  )
}

/**
 * Hook personalizado para manejar notificaciones
 */
export const useTourNotifications = () => {
  const [notifications, setNotifications] = useState([])

  const addNotification = (notification) => {
    const id = Date.now()
    const newNotification = {
      id,
      ...notification,
      open: true
    }
    
    setNotifications(prev => [...prev, newNotification])
    
    return id
  }

  const removeNotification = (id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, open: false } : notif
      )
    )
    
    // Limpiar después de la animación
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id))
    }, 300)
  }

  const showSuccess = (message, title = 'Success') => {
    return addNotification({
      type: 'success',
      message,
      title,
      duration: 4000
    })
  }

  const showError = (message, title = 'Error') => {
    return addNotification({
      type: 'error',
      message,
      title,
      duration: 6000
    })
  }

  const showWarning = (message, title = 'Warning') => {
    return addNotification({
      type: 'warning',
      message,
      title,
      duration: 5000
    })
  }

  const showInfo = (message, title = 'Info') => {
    return addNotification({
      type: 'info',
      message,
      title,
      duration: 4000
    })
  }

  const showPersistent = (message, title = 'Important') => {
    return addNotification({
      type: 'info',
      message,
      title,
      persistent: true,
      autoHide: false
    })
  }

  return {
    notifications,
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPersistent
  }
}

/**
 * Componente contenedor para múltiples notificaciones
 */
export const TourNotificationContainer = ({ notifications, onRemove }) => {
  return (
    <div>
      {notifications.map(notification => (
        <TourNotification
          key={notification.id}
          open={notification.open}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          duration={notification.duration}
          autoHide={notification.autoHide}
          persistent={notification.persistent}
          onClose={() => onRemove(notification.id)}
        />
      ))}
    </div>
  )
}

export default TourNotification 


// WEBPACK FOOTER //
// ./src/features/tours/components/TourNotification.js